import { useEffect, useState } from "react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { UserPlus, Copy, Trash2, Clock } from "lucide-react";
import { StatusBadge, PageHeader, formatDate } from "@/lib/ui";

const ROLES = [
  { value: "org_admin", label: "Administrator" },
  { value: "intake_coordinator", label: "Intake coordinator" },
  { value: "staff", label: "Staff" },
  { value: "read_only", label: "Viewer" },
];

export default function Team() {
  const { currentOrgId, user, currentRole } = useAuth();
  const isAdmin = currentRole === "org_admin";
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [inviteeName, setInviteeName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("staff");
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [m, i] = await Promise.all([
      supabase.from("organization_members").select("id,role,status,joined_at,user_id,profile:profiles!organization_members_user_id_fkey(id,full_name)").eq("organization_id", currentOrgId),
      supabase.from("invitations").select("id,email,invitee_name,role,expires_at,accepted_at,token,last_email_status,last_email_error,last_email_sent_at").eq("organization_id", currentOrgId).is("accepted_at", null),
    ]);
    setMembers(m.data || []); setInvites(i.data || []); setLoading(false);
  };
  useEffect(() => { if (currentOrgId) load(); }, [currentOrgId]);

  const invite = async () => {
    if (!inviteeName.trim()) return toast.error("Name is required");
    if (!email.trim()) return toast.error("Email is required");
    setSaving(true);
    try {
      const r = await apiFetch("/invitations", { method: "POST", body: JSON.stringify({
        organization_id: currentOrgId, email, role, invitee_name: inviteeName.trim(),
      }) });
      setLastResult(r);
      if (r.email_sent) {
        toast.success(`Invitation emailed to ${email}`);
        setEmail(""); setInviteeName(""); setRole("staff"); setOpen(false);
      } else if (!r.email_configured) {
        // Email service is not configured — leave dialog open so user can copy link
        toast.error("Email service not configured — email was NOT sent. Copy the invitation link below and share it manually.", { duration: 10000 });
      } else {
        toast.error(`Email delivery failed: ${r.email_error || "unknown error"} — copy the link below.`, { duration: 10000 });
      }
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const changeRole = async (id, r) => {
    try { await apiFetch(`/members/${id}`, { method: "PATCH", body: JSON.stringify({ role: r }) }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const removeMember = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try { await apiFetch(`/members/${id}`, { method: "DELETE" }); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/accept-invite?token=${token}`);
    toast.success("Invite link copied");
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="team-page">
      <PageHeader title="Team" subtitle="Invite teammates and manage their access."
        actions={isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-btn" className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><UserPlus size={16} className="mr-1.5" /> Invite team member</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-display tracking-tight">Invite team member</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Name *</Label><Input data-testid="invite-name" value={inviteeName} onChange={(e) => setInviteeName(e.target.value)} className="mt-1.5" placeholder="Sarah Johnson" /></div>
                <div><Label>Email *</Label><Input type="email" data-testid="invite-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="teammate@company.com" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {lastResult && !lastResult.email_sent && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <div className="font-medium text-amber-900 mb-2">
                      {!lastResult.email_configured
                        ? "Email service is not configured on this workspace."
                        : "Email delivery failed."}
                    </div>
                    <div className="text-xs text-amber-800 mb-3">
                      {!lastResult.email_configured
                        ? "Set EMERGENT_EMAIL_KEY in /app/backend/.env to enable email delivery. Meanwhile, copy the invitation link below and send it manually:"
                        : `Error: ${lastResult.email_error || "unknown"}`}
                    </div>
                    <div className="flex gap-2">
                      <input readOnly value={lastResult.accept_link} className="flex-1 text-xs px-2 py-1.5 border border-amber-200 rounded font-mono bg-white" data-testid="invite-link-input" />
                      <Button size="sm" variant="outline" data-testid="copy-invite-link" onClick={() => { navigator.clipboard.writeText(lastResult.accept_link); toast.success("Link copied"); }}>Copy</Button>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="send-invite-btn" onClick={invite} disabled={saving} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">{saving ? "Sending…" : "Send invitation"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E8E2D9]"><h3 className="font-display font-semibold text-sm uppercase tracking-widest text-[#57534E]">Team members ({members.length})</h3></div>
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow className="border-b border-[#E8E2D9]"><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Joined</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                    <TableCell className="font-medium">
                      {m.profile?.full_name || <span className="text-[#A8A29E]">(no name)</span>}
                      {m.user_id === user?.id && <span className="ml-2 text-xs text-[#E8712C]">You</span>}
                    </TableCell>
                    <TableCell>
                      {isAdmin && m.user_id !== user?.id ? (
                        <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                          <SelectTrigger className="w-44 h-8 text-xs" data-testid={`role-${m.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <span className="text-sm capitalize">{m.role.replace(/_/g, " ")}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#57534E]">{formatDate(m.joined_at)}</TableCell>
                    <TableCell><StatusBadge value={m.status} /></TableCell>
                    <TableCell className="text-right">
                      {isAdmin && m.user_id !== user?.id && (
                        <Button size="sm" variant="ghost" onClick={() => removeMember(m.id)} className="text-red-600" data-testid={`remove-${m.id}`}><Trash2 size={14} /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {/* Mobile card layout (<md) */}
            <div className="md:hidden divide-y divide-[#E8E2D9]">
              {members.map((m) => (
                <div key={m.id} data-testid={`member-card-${m.id}`} className="px-4 py-3 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FDE9D8] flex items-center justify-center text-[#E8712C] font-bold text-sm flex-shrink-0">
                    {(m.profile?.full_name?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-sm truncate">
                        {m.profile?.full_name || <span className="text-[#A8A29E]">(no name)</span>}
                      </div>
                      {m.user_id === user?.id && <span className="text-[10px] text-[#E8712C] uppercase tracking-widest">You</span>}
                      <StatusBadge value={m.status} />
                    </div>
                    <div className="text-xs text-[#57534E] mt-1 capitalize">{m.role.replace(/_/g, " ")}</div>
                    <div className="text-xs text-[#A8A29E] mt-0.5">Joined {formatDate(m.joined_at)}</div>
                    {isAdmin && m.user_id !== user?.id && (
                      <div className="flex gap-2 mt-2">
                        <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                          <SelectTrigger className="h-8 text-xs flex-1" data-testid={`role-m-${m.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" onClick={() => removeMember(m.id)} className="text-red-600" data-testid={`remove-m-${m.id}`}><Trash2 size={14} /></Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {invites.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
              <div className="px-6 py-4 border-b border-[#E8E2D9]"><h3 className="font-display font-semibold text-sm uppercase tracking-widest text-[#57534E] flex items-center gap-2"><Clock size={14} className="text-[#E8712C]" />Pending invitations</h3></div>
              <Table className="hidden md:table">
                <TableHeader><TableRow className="border-b border-[#E8E2D9]"><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Expires</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {invites.map((i) => (
                    <TableRow key={i.id} data-testid={`invite-row-${i.id}`}>
                      <TableCell>{i.email}</TableCell>
                      <TableCell className="text-sm capitalize">{i.role.replace(/_/g, " ")}</TableCell>
                      <TableCell className="text-sm text-[#57534E]">{formatDate(i.expires_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => copyLink(i.token)} data-testid={`copy-${i.id}`}><Copy size={12} className="mr-1" /> Copy link</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="md:hidden divide-y divide-[#E8E2D9]">
                {invites.map((i) => (
                  <div key={i.id} data-testid={`invite-card-${i.id}`} className="px-4 py-3">
                    <div className="text-sm font-medium truncate">{i.email}</div>
                    <div className="text-xs text-[#57534E] mt-0.5 capitalize">{i.role.replace(/_/g, " ")} · expires {formatDate(i.expires_at)}</div>
                    <Button size="sm" variant="outline" className="rounded-full mt-2" onClick={() => copyLink(i.token)} data-testid={`copy-m-${i.id}`}><Copy size={12} className="mr-1" /> Copy link</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
